import React, { useState } from "react";
import { MapPin, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { GeoRadiusFilter, isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { useLocalMode } from "@/context/LocalModeContext";

/**
 * A slim, persistent bar shown beneath the main nav on the six supported pages.
 * Always renders the REFRESH control; the Local Mode toggle only appears when the
 * server feature flag makes Local Mode visible to this user.
 */
export const LocalModeBar = () => {
  const lm = useLocalMode();
  const [spinning, setSpinning] = useState(false);

  const { visible, enabled, center, radius, setEnabled, applyGeoPatch, refresh } = lm;
  const activeLabel = center
    ? (isRegionCenter(center) ? center.label : `${center.label} · ${radius} mi`)
    : null;

  const handleRefresh = () => {
    setSpinning(true);
    refresh();
    setTimeout(() => setSpinning(false), 700);
  };

  return (
    <div className="bg-secondary/30" data-testid="local-mode-bar">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-end gap-2 px-4 py-2 sm:px-6 lg:px-8">
        {visible && (
          <>
            <MapPin className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-sm font-[600] text-foreground">Local Mode</span>
            {enabled && (
              activeLabel ? (
                <span className="max-w-[220px] truncate text-sm text-muted-foreground" data-testid="local-mode-active-label">
                  {activeLabel}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground" data-testid="local-mode-choose">Pick a location</span>
              )
            )}
            {enabled && (
              <GeoRadiusFilter
                value={{ geoOn: true, center, radius }}
                onChange={applyGeoPatch}
                testId="local-mode-geo"
              />
            )}
            <span className="ml-1 text-xs text-muted-foreground">{enabled ? "On" : "Off"}</span>
            <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Local Mode"
              data-testid="local-mode-toggle" />
          </>
        )}
        <button
          type="button"
          onClick={handleRefresh}
          aria-label="Refresh"
          data-testid="local-mode-refresh"
          className="ml-2 inline-flex items-center gap-1.5 bg-transparent text-muted-foreground transition-colors hover:text-primary"
        >
          <RefreshCw className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} />
          <span className="text-xs font-[600] uppercase tracking-wide">Refresh</span>
        </button>
      </div>
    </div>
  );
};

export default LocalModeBar;
